import {
  createElement,
  forwardRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { filterByCharMode, type CharModeOptions } from './inputCharMode';
import './FieldStatus.scss';

interface FormFieldProps {
  className?: string;
  /** Angular `matPrefix` content. */
  prefix?: ReactNode;
  /** Angular `matSuffix` content (icons, datepicker toggle). */
  suffix?: ReactNode;
  /** The control (an `Input`/`Textarea`). */
  children: ReactNode;
  /** Angular `ng-invalid ng-touched`: draws the red outline. */
  invalid?: boolean;
  disabled?: boolean;
  /** The control is read-only: drawn grey like a disabled one (see FieldStatus.scss). */
  readOnly?: boolean;
  focused?: boolean;
  onWrapperClick?: () => void;
}

/** The `<mat-form-field>` host element, so the legacy `mat-form-field {}` rules apply as in Angular. */
function MatFormField({ className, children }: { className: string; children: ReactNode }) {
  return createElement('mat-form-field', { className }, children);
}

/** `<mat-form-field appearance="outline">`: same DOM and class names as Material's MDC field. */
export function FormField({
  className = '',
  prefix,
  suffix,
  children,
  invalid = false,
  disabled = false,
  readOnly = false,
  focused = false,
  onWrapperClick,
}: FormFieldProps) {
  const classes = [
    'mat-mdc-form-field',
    'mat-mdc-form-field-type-mat-input',
    suffix && 'mat-mdc-form-field-has-icon-suffix',
    prefix && 'mat-mdc-form-field-has-icon-prefix',
    'mat-form-field-appearance-outline',
    'mat-primary',
    focused && 'mat-focused',
    disabled && 'mat-form-field-disabled',
    readOnly && !disabled && 'kit-field-readonly',
    invalid && 'ng-invalid ng-touched mat-form-field-invalid',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <MatFormField className={classes}>
      <div
        className={`mat-mdc-text-field-wrapper mdc-text-field mdc-text-field--outlined mdc-text-field--no-label${disabled ? ' mdc-text-field--disabled' : ''}${focused ? ' mdc-text-field--focused' : ''}${invalid ? ' mdc-text-field--invalid' : ''}`}
        onClick={onWrapperClick}
      >
        <div className="mat-mdc-form-field-flex">
          <div className="mdc-notched-outline mdc-notched-outline--no-label">
            <div className="mdc-notched-outline__leading"></div>
            <div className="mdc-notched-outline__notch"></div>
            <div className="mdc-notched-outline__trailing"></div>
          </div>
          {prefix && <div className="mat-mdc-form-field-icon-prefix">{prefix}</div>}
          <div className="mat-mdc-form-field-infix">{children}</div>
          {suffix && <div className="mat-mdc-form-field-icon-suffix">{suffix}</div>}
        </div>
      </div>
      <div className="mat-mdc-form-field-subscript-wrapper mat-mdc-form-field-bottom-align">
        <div className="mat-mdc-form-field-hint-wrapper">
          <div className="mat-mdc-form-field-hint-spacer"></div>
        </div>
      </div>
    </MatFormField>
  );
}

interface InputFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'>, CharModeOptions {
  fieldClassName?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  invalid?: boolean;
}

/** `<mat-form-field><input matInput ...></mat-form-field>` in one component; forwards its ref (react-hook-form). */
export const InputField = forwardRef<HTMLInputElement, InputFieldProps>(function InputField(
  {
    fieldClassName,
    prefix,
    suffix,
    invalid,
    className = '',
    onFocus,
    onBlur,
    onChange,
    charMode,
    allowDecimal,
    allowNegative,
    maxIntDigits,
    maxDecimals,
    extraChars,
    ...rest
  },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (charMode) {
      const filtered = filterByCharMode(event.target.value, {
        charMode,
        allowDecimal,
        allowNegative,
        maxIntDigits,
        maxDecimals,
        extraChars,
      });
      if (filtered !== event.target.value) {
        event.target.value = filtered;
      }
    }
    onChange?.(event);
  };
  return (
    <FormField
      className={fieldClassName}
      prefix={prefix}
      suffix={suffix}
      invalid={invalid}
      disabled={rest.disabled}
      readOnly={rest.readOnly}
      focused={focused}
    >
      <input
        ref={ref}
        className={`mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control ${className}`.trim()}
        autoComplete="off"
        {...rest}
        onChange={handleChange}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
      />
    </FormField>
  );
});

interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, CharModeOptions {
  fieldClassName?: string;
  invalid?: boolean;
}

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(function TextareaField(
  {
    fieldClassName,
    invalid,
    className = '',
    onFocus,
    onBlur,
    onChange,
    charMode,
    allowDecimal,
    allowNegative,
    maxIntDigits,
    maxDecimals,
    extraChars,
    ...rest
  },
  ref,
) {
  const [focused, setFocused] = useState(false);
  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (charMode) {
      const filtered = filterByCharMode(event.target.value, {
        charMode,
        allowDecimal,
        allowNegative,
        maxIntDigits,
        maxDecimals,
        extraChars,
      });
      if (filtered !== event.target.value) {
        event.target.value = filtered;
      }
    }
    onChange?.(event);
  };
  return (
    <FormField
      className={fieldClassName}
      invalid={invalid}
      disabled={rest.disabled}
      readOnly={rest.readOnly}
      focused={focused}
    >
      <textarea
        ref={ref}
        className={`mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control ${className}`.trim()}
        autoComplete="off"
        {...rest}
        onChange={handleChange}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
      />
    </FormField>
  );
});
